import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  ViewChild,
} from '@angular/core';
import { EventLifeLine } from '../../models/life-line-models';
import * as d3 from 'd3';
import { AxisDomain } from 'd3';

@Component({
  selector: 'app-lifeline-section',
  templateUrl: './lifeline-section.component.html',
  styleUrls: ['./lifeline-section.component.scss'],
  standalone: false,
})
export class LifelineSectionComponent implements AfterViewInit {
  @Input() significantEvents: EventLifeLine[] = [];
  @Input() patientNameAndSurname: string = '';
  @ViewChild('chart', { static: true })
  private chartContainer!: ElementRef;

  private svg: any;
  private g: any;
  private xAxisGroup: any;
  private margin = { top: 50, right: 200, bottom: 50, left: 100 };
  private width: number = 400;
  private height: number = 800;
  private columnWidth = 40; // Width for each event column
  private scrollHeight: number = 1200; // For vertical scrolling
  private yScale: any; // Changed from xScale
  private mainLine: any; // To store the main vertical line
  public isLoading: boolean = true;

  constructor() {}

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.createChart();
    }, 1000);
  }

  private parseEventDate(dateStr: string, isEndDate: boolean = false): Date {
    if (!dateStr) return new Date();

    const parts = dateStr.split('/');
    if (parts.length === 2) {
      // Format is MM/YYYY
      const month = parseInt(parts[0]) - 1; // months are 0-based in JS
      const year = parseInt(parts[1]);
      if (isEndDate) {
        // Last day of the month for end dates
        return new Date(year, month + 1, 0);
      } else {
        // First day of the month for start dates
        return new Date(year, month, 1);
      }
    } else {
      // Format is DD/MM/YYYY
      return d3.timeParse('%d/%m/%Y')(dateStr) || new Date();
    }
  }

  private calculateScrollHeight(): number {
    // Count events (duration events count as 2)
    const eventCount = this.significantEvents.reduce((count, event) => {
      // Add 1 for each event, plus 1 extra for duration events
      const eventPoints = event.end_date ? 2 : 1;
      // Add sub-events
      const subEventPoints = event.sub_events?.length || 0;
      return count + eventPoints + subEventPoints;
    }, 0);

    // Calculate height based on number of points and minimum spacing
    const minSpacing = 50; // Minimum pixels between events
    return eventCount * minSpacing;
  }

  private createChart() {
    const element = this.chartContainer.nativeElement;
    this.width =
      element.offsetWidth - this.margin.left - this.margin.right + 200;

    // Get all dates in order
    const allDates = this.significantEvents
      .flatMap((event) => {
        const dates = [
          { date: this.parseEventDate(event.start_date), id: event.id },
        ];
        if (event.end_date) {
          dates.push({
            date: this.parseEventDate(event.end_date, true),
            id: event.id,
          });
        }
        event.sub_events?.forEach((subEvent) => {
          dates.push({
            date: this.parseEventDate(subEvent.start_date),
            id: subEvent.id,
          });
        });
        return dates;
      })
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    // Calculate height based on number of events
    this.scrollHeight = this.calculateScrollHeight();

    // Create container and SVG
    const container = d3
      .select(element)
      .append('div')
      .style('display', 'flex')
      .style('justify-content', 'center')
      .style('position', 'relative');

    this.svg = container
      .append('svg')
      .attr('width', this.width + this.margin.left + this.margin.right)
      .attr('height', this.scrollHeight + this.margin.top + this.margin.bottom);

    this.g = this.svg
      .append('g')
      .attr('transform', `translate(${this.margin.left},${this.margin.top})`);

    // Create scale based on index rather than time
    this.yScale = (date: Date) => {
      const index = allDates.findIndex(
        (d) => d.date.getTime() === date.getTime()
      );
      return index * 50; // 50 pixels between each date point
    };

    // Draw main vertical line
    this.mainLine = this.g
      .append('line')
      .attr('class', 'timeline-line')
      .attr('x1', 0)
      .attr('x2', 0)
      .attr('y1', 0)
      .attr('y2', this.scrollHeight)
      .style('stroke', '#000')
      .style('stroke-width', 2);

    this.renderEvents();
    this.renderLegend();
    this.isLoading = false;
  }

  private renderEvents() {
    const columns: { [key: number]: { date: Date; end?: Date }[] } = {};
    const eventColumns: { [key: string]: number } = {}; // Track column for each event
    let maxColumn = 0;

    this.significantEvents.forEach((event) => {
      const startDate = this.parseEventDate(event.start_date);
      const endDate = event.end_date
        ? this.parseEventDate(event.end_date, true)
        : null;
      const eventColor = event.color || '#87CEFA';

      // Find available column
      let column = 0;
      while (
        columns[column]?.some((e) =>
          this.datesOverlap(
            startDate,
            endDate || startDate,
            e.date,
            e.end || e.date
          )
        )
      ) {
        column++;
      }
      maxColumn = Math.max(maxColumn, column);

      if (!columns[column]) columns[column] = [];
      columns[column].push({ date: startDate, end: endDate || undefined });

      const xPosition = (column + 1) * this.columnWidth;

      // Add start date text
      this.g
        .append('text')
        .attr('class', 'event-date')
        .attr('x', -10)
        .attr('y', this.yScale(startDate))
        .attr('text-anchor', 'end')
        .attr('dy', '.35em')
        .text(event.start_date)
        .style('font-size', '11px')
        .style('fill', '#666');

      // Add end date text if exists
      if (endDate) {
        this.g
          .append('text')
          .attr('class', 'event-date')
          .attr('x', -10)
          .attr('y', this.yScale(endDate))
          .attr('text-anchor', 'end')
          .attr('dy', '.35em')
          .text(event.end_date)
          .style('font-size', '11px')
          .style('fill', '#666');
      }

      // Add sub-event dates
      event.sub_events?.forEach((subEvent) => {
        const subDate = this.parseEventDate(subEvent.start_date);
        this.g
          .append('text')
          .attr('class', 'event-date')
          .attr('x', -10)
          .attr('y', this.yScale(subDate))
          .attr('text-anchor', 'end')
          .attr('dy', '.35em')
          .text(subEvent.start_date)
          .style('font-size', '11px')
          .style('fill', '#666');
      });

      // Render main event
      if (endDate) {
        // Draw colored rectangle for duration events
        this.g
          .append('rect')
          .attr('class', 'event-duration')
          .attr('x', 0)
          .attr('y', this.yScale(startDate))
          .attr('width', xPosition)
          .attr('height', this.yScale(endDate) - this.yScale(startDate))
          .style('fill', eventColor)
          .style('opacity', 0.3);

        // Draw border lines
        this.g
          .append('line')
          .attr('class', 'event-border')
          .attr('x1', 0)
          .attr('x2', xPosition)
          .attr('y1', this.yScale(startDate))
          .attr('y2', this.yScale(startDate))
          .style('stroke', eventColor)
          .style('stroke-width', 2);

        this.g
          .append('line')
          .attr('class', 'event-border')
          .attr('x1', xPosition)
          .attr('x2', xPosition)
          .attr('y1', this.yScale(startDate))
          .attr('y2', this.yScale(endDate))
          .style('stroke', eventColor)
          .style('stroke-width', 2);
      } else {
        // Single point event
        this.g
          .append('circle')
          .attr('class', 'event-point')
          .attr('cx', xPosition)
          .attr('cy', this.yScale(startDate))
          .attr('r', 5)
          .style('fill', eventColor);
      }

      // Handle sub-events
      event.sub_events?.forEach((subEvent) => {
        const subDate = this.parseEventDate(subEvent.start_date);

        // Draw sub-event point in same column as parent but at correct date
        this.g
          .append('circle')
          .attr('class', 'subevent-point')
          .attr('cx', xPosition)
          .attr('cy', this.yScale(subDate))
          .attr('r', 3)
          .style('fill', '#666');
      });

      eventColumns[event.id] = column; // Store the column number for this event
    });

    // Calculate title positions after all events are rendered
    const titleX = (maxColumn + 2) * this.columnWidth;
    const subTitleX = titleX + 200;

    // Create an array of all title positions to manage spacing
    interface TitlePosition {
      y: number;
      text: string;
      isSubEvent: boolean;
      hasEndDate: boolean;
      xPosition?: number;
      originalY: number;
      parentId: string;
    }

    const titlePositions: TitlePosition[] = [];

    // First collect all title positions
    this.significantEvents.forEach((event) => {
      const startDate = this.parseEventDate(event.start_date);
      const xPosition = (eventColumns[event.id] + 1) * this.columnWidth;
      const y = this.yScale(startDate);

      titlePositions.push({
        y,
        text: event.title,
        isSubEvent: false,
        hasEndDate: !!event.end_date,
        xPosition,
        originalY: y,
        parentId: event.id,
      });

      event.sub_events?.forEach((subEvent) => {
        const subDate = this.parseEventDate(subEvent.start_date);
        titlePositions.push({
          y: this.yScale(subDate),
          text: `${subEvent.title}`,
          isSubEvent: true,
          hasEndDate: false,
          originalY: this.yScale(subDate),
          parentId: event.id,
        });
      });
    });

    // Sort by vertical position
    titlePositions.sort((a, b) => a.y - b.y);

    // Adjust positions to ensure minimum spacing
    const minSpacing = 20;
    for (let i = 1; i < titlePositions.length; i++) {
      const prevY = titlePositions[i - 1].y;
      const currentY = titlePositions[i].y;
      if (currentY - prevY < minSpacing) {
        titlePositions[i].y = prevY + minSpacing;
      }
    }

    // Render titles with adjusted positions
    titlePositions.forEach((pos) => {
      // Main event title or sub-event title (all in same column)
      this.g
        .append('text')
        .attr('class', pos.isSubEvent ? 'subevent-title' : 'event-title')
        .attr('x', titleX)
        .attr('y', pos.y)
        .attr('dy', '.35em')
        .text(pos.text)
        .style('font-size', pos.isSubEvent ? '11px' : '12px')
        .style('font-weight', pos.hasEndDate ? 'bold' : 'normal')
        .style('fill', pos.isSubEvent ? '#666' : '#000');

      // Add dotted line for all events
      if (pos.xPosition) {
        this.g
          .append('line')
          .attr('class', 'title-connector')
          .attr('x1', pos.xPosition)
          .attr('x2', titleX - 10)
          .attr('y1', pos.originalY)
          .attr('y2', pos.y)
          .style('stroke', '#666')
          .style('stroke-width', 1)
          .style('stroke-dasharray', '3,3');
      }
    });

    // Update SVG width (reduced since we don't need second column)
    const totalWidth = titleX + 700;
    this.svg.attr('width', totalWidth);
  }

  private renderLegend() {
    // First create a background rectangle for the legend
    const legendPadding = 10;
    const legendBackground = this.svg
      .append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${this.svg.attr('width') - 320}, 20)`);

    // Add white background
    legendBackground
      .append('rect')
      .attr('class', 'legend-bg')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', 300)
      .attr('height', 0)
      .attr('fill', 'white')
      .attr('stroke', '#ccc')
      .attr('rx', 5);

    const legendGroup = legendBackground
      .append('g')
      .attr('transform', `translate(${legendPadding}, ${legendPadding})`);

    // Add legend title
    legendGroup
      .append('text')
      .attr('class', 'legend-title')
      .attr('x', 0)
      .attr('y', 0)
      .attr('transform', 'translate(0, 10)')
      .text('Eventi')
      .style('font-size', '14px')
      .style('font-weight', 'bold');

    let currentY = 40;
    const lineHeight = 20;

    // Sort events by start date
    const sortedEvents = [...this.significantEvents].sort((a, b) => {
      const dateA = this.parseEventDate(a.start_date);
      const dateB = this.parseEventDate(b.start_date);
      return dateA.getTime() - dateB.getTime();
    });

    sortedEvents.forEach((event, index) => {
      // Rest of the legend rendering code remains the same...
      const eventGroup = legendGroup
        .append('g')
        .attr('transform', `translate(0, ${currentY})`);

      eventGroup
        .append('text')
        .attr('x', 0)
        .attr('y', 0)
        .text(event.title)
        .style('font-size', '12px')
        .style('font-weight', event.end_date ? 'bold' : 'normal');

      eventGroup
        .append('text')
        .attr('x', 0)
        .attr('y', lineHeight)
        .text(
          `${event.start_date}${event.end_date ? ' - ' + event.end_date : ''}`
        )
        .style('font-size', '11px')
        .style('fill', '#666');

      currentY += lineHeight * 2;

      // Sort sub-events by start date as well
      const sortedSubEvents = event.sub_events
        ? [...event.sub_events].sort((a, b) => {
            const dateA = this.parseEventDate(a.start_date);
            const dateB = this.parseEventDate(b.start_date);
            return dateA.getTime() - dateB.getTime();
          })
        : [];

      sortedSubEvents.forEach((subEvent) => {
        const subEventGroup = legendGroup
          .append('g')
          .attr('transform', `translate(15, ${currentY})`);

        subEventGroup
          .append('text')
          .attr('x', 0)
          .attr('y', 0)
          .text(`↳ ${subEvent.title}`)
          .style('font-size', '11px')
          .style('fill', '#666');

        subEventGroup
          .append('text')
          .attr('x', 0)
          .attr('y', lineHeight)
          .text(subEvent.start_date)
          .style('font-size', '11px')
          .style('fill', '#666');

        currentY += lineHeight * 2;
      });

      currentY += 10;
    });

    // Update background height based on content
    legendBackground
      .select('.legend-bg')
      .attr('height', currentY + legendPadding * 2);
  }

  printChart() {
    const svgElement = this.chartContainer.nativeElement.querySelector('svg');
    const clonedSvg = svgElement.cloneNode(true) as SVGElement;

    // Remove the legend from the cloned SVG
    const legendElement = clonedSvg.querySelector('.legend');
    if (legendElement) {
      legendElement.remove();
    }

    // Add test text at the start of SVG
    const testText = document.createElementNS(
      'http://www.w3.org/2000/svg',
      'text'
    );
    testText.setAttribute('x', '10');
    testText.setAttribute('y', '20');
    testText.textContent = `${this.patientNameAndSurname} - Linea di vita`;
    clonedSvg.insertBefore(testText, clonedSvg.firstChild);

    const printWindow = window.open('', '', 'height=600,width=800');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <style>
              @media print {
                @page {
                  size: A4 portrait;
                  margin: 15mm;
                }
                body {
                  margin: 0;
                  padding: 0;
                }
                .container {
                  width: 100%;
                }
                .print-content {
                  display: block;
                  height: auto;
                  margin-top: 0;
                }
                svg {
                  width: 180mm !important;
                  height: ${
                    this.scrollHeight + this.margin.top + this.margin.bottom
                  }px !important;
                  display: block;
                  margin: 0;
                  overflow: visible;
                  position: relative;
                  top: 0;
                }
              
                g {
                  break-inside: avoid;
                }
              }
              body {
                font-family: Arial, sans-serif;
              }
            </style>
          </head>
          <body>
            <div class="container">
              <div class="print-content">
                ${clonedSvg.outerHTML}
              </div>
            </div>
          </body>
        </html>
      `);

      printWindow.document.close();
      printWindow.focus();

      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 2000);
    }
  }

  private datesOverlap(
    start1: Date,
    end1: Date,
    start2: Date,
    end2: Date
  ): boolean {
    return start1 <= end2 && end1 >= start2;
  }
}
